import { workspaceKey } from "./workspace"

type State =
  | { status: "pending" }
  | { status: "ready" }
  | { status: "failed"; message: string }

export const WORKTREE_STATE_MAX_ENTRIES = 200
const WORKTREE_WAITER_MAX_ENTRIES = 100

export function createWorktreeState() {
  const state = new Map<string, State>()
  const waiters = new Map<string, { promise: Promise<State>; resolve: (state: State) => void }>()

  const removeWaiter = (key: string, message: string) => {
    const waiter = waiters.get(key)
    if (!waiter) return
    waiters.delete(key)
    waiter.resolve({ status: "failed", message })
  }
  const touchState = (key: string, value: State) => {
    state.delete(key)
    state.set(key, value)
    while (state.size > WORKTREE_STATE_MAX_ENTRIES) {
      const stale = state.keys().next().value
      if (!stale) break
      state.delete(stale)
      removeWaiter(stale, "Worktree state was evicted")
    }
  }
  const settle = (directory: string, next: State) => {
    const key = workspaceKey(directory)
    touchState(key, next)
    const waiter = waiters.get(key)
    if (!waiter) return
    waiters.delete(key)
    waiter.resolve(next)
  }

  return {
    get(directory: string) {
      const key = workspaceKey(directory)
      const value = state.get(key)
      if (value) touchState(key, value)
      return value
    },
    pending(directory: string) {
      const key = workspaceKey(directory)
      const current = state.get(key)
      if (current && current.status !== "pending") return
      touchState(key, { status: "pending" })
    },
    ready(directory: string) {
      settle(directory, { status: "ready" })
    },
    failed(directory: string, message: string) {
      settle(directory, { status: "failed", message })
    },
    wait(directory: string, signal?: AbortSignal) {
      const key = workspaceKey(directory)
      const current = state.get(key)
      if (current && current.status !== "pending") return Promise.resolve(current)
      const existing = waiters.get(key)
      if (existing) return existing.promise

      let resolve!: (state: State) => void
      const promise = new Promise<State>((done) => { resolve = done })
      waiters.set(key, { promise, resolve })
      while (waiters.size > WORKTREE_WAITER_MAX_ENTRIES) {
        const stale = waiters.keys().next().value
        if (!stale) break
        removeWaiter(stale, "Worktree wait was evicted")
      }
      if (signal) {
        const abort = () => {
          if (waiters.get(key)?.promise !== promise) return
          removeWaiter(key, "aborted")
        }
        signal.addEventListener("abort", abort, { once: true })
        void promise.finally(() => signal.removeEventListener("abort", abort))
        if (signal.aborted) abort()
      }
      return promise
    },
    clear(directory: string) {
      const key = workspaceKey(directory)
      state.delete(key)
      removeWaiter(key, "Worktree state was cleared")
    },
    sizes: () => ({ state: state.size, waiters: waiters.size }),
    dispose() {
      for (const key of [...waiters.keys()]) removeWaiter(key, "Worktree state was disposed")
      state.clear()
    },
  }
}

// Worktree events and prompt submission meet outside a shared component tree.
// App server lifecycle composition explicitly disposes this bounded owner.
export const Worktree = createWorktreeState()
